import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { useReducedMotion } from '@wix/da-hooks/pkg/useReducedMotion';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { ThemeContext, ThemeSkinContext } from '@wix/da-react-context/pkg/ThemeContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import TierIcon from '@wix/da-ds/pkg/Icons/24x24/Tier';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/ArrowDown';
import { useSubscriptionLockFTUE } from '../useSubscriptionLockFTUE';
import s from './SubscriptionLockLitFTUE.scss';
/** Displays a ftue popup for first time viewers of in-tier, lit/journal deviations
 *  from ArtStage/DesktopTextualArtStage that tells the user to scroll down to subscribe. */ export const SubscriptionLockLitFTUE = ({ className, deviation })=>{
    const { t } = useTranslation();
    const isMounted = useIsMounted();
    // ...seems like all of this could have been simpler since it's svg
    const theme = useContext(ThemeContext);
    const themeSkin = useContext(ThemeSkinContext);
    let hexagonTheme = theme;
    if (themeSkin === 'light-green') {
        hexagonTheme = 'green';
    }
    const hexagonUrl = Url.staticCDNLink(`eclipse/global/svg/hexagon-${hexagonTheme}.svg`);
    const fellaUrl = Url.staticCDNLink(`eclipse/global/svg/fella-pointing-down${theme === 'light' ? '-light' : ''}.svg`);
    const rootRef = useRef(null);
    const isMobile = useContext(MobileContext);
    const { isSubscriptionLockFTUEShown: isFlagUp, dropFlag } = useSubscriptionLockFTUE(deviation);
    const [isShown, setIsShown] = useState(!isMobile && isFlagUp);
    const prefersReducedMotion = useReducedMotion();
    const [fadingOut, setFadingOut] = useState(false);
    const dropFlagAndFadeOut = useCallback(()=>{
        if (!rootRef.current || !isShown) {
            return;
        }
        dropFlag();
        if (prefersReducedMotion) {
            setIsShown(false);
            return;
        }
        const handler = (e)=>{
            e.target === rootRef.current && setIsShown(false);
        };
        rootRef.current.addEventListener('transitionend', handler);
        setFadingOut(true);
        return ()=>{
            var _rootRef_current;
            (_rootRef_current = rootRef.current) === null || _rootRef_current === void 0 ? void 0 : _rootRef_current.removeEventListener('transitionend', handler);
        };
    }, [
        rootRef,
        isShown,
        prefersReducedMotion,
        dropFlag
    ]);
    useEffect(()=>{
        isShown && typeof window !== 'undefined' && window.addEventListener('scroll', dropFlagAndFadeOut, {
            once: true
        });
        return ()=>window.removeEventListener('scroll', dropFlagAndFadeOut);
    }, [
        isShown,
        dropFlagAndFadeOut
    ]);
    if (!isShown) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['root'], isMounted && s['ready'], fadingOut && s['fading-out'], className),
        onClick: dropFlagAndFadeOut,
        ref: rootRef
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['fella-popup']
    }, /*#__PURE__*/ React.createElement("img", {
        className: s['hexagon'],
        src: hexagonUrl,
        alt: ""
    }), /*#__PURE__*/ React.createElement("span", {
        className: s['fake-close-button']
    }, /*#__PURE__*/ React.createElement(CloseIcon, null)), /*#__PURE__*/ React.createElement("section", {
        className: s['ftue-explanation']
    }, /*#__PURE__*/ React.createElement(TierIcon, {
        className: s['tier-icon']
    }), /*#__PURE__*/ React.createElement("p", null, t('deviation.tier.ftue.subscriptionLockLit')), /*#__PURE__*/ React.createElement("p", {
        className: s['fake-link']
    }, t('common.gotIt'))), /*#__PURE__*/ React.createElement("img", {
        className: s['fella'],
        src: fellaUrl,
        alt: ""
    }), /*#__PURE__*/ React.createElement("div", {
        className: s['arrows-down']
    }, /*#__PURE__*/ React.createElement(ArrowDownIcon, null), /*#__PURE__*/ React.createElement(ArrowDownIcon, null))));
};
SubscriptionLockLitFTUE.displayName = 'SubscriptionLockLitFTUE';
export default SubscriptionLockLitFTUE;

//# sourceMappingURL=SubscriptionLockLitFTUE.js.map